// Hook per la preferenza "mostra testo originale" nell'admin.
// Usa localStorage con fallback in-memory se non disponibile (browser privato, cookie blocked).

import { useState, useCallback, useEffect } from 'react';

const KEY = 'audiq.admin.showOriginal';

function readInitial(): boolean {
  try {
    return window.localStorage.getItem(KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Hook per gestire la preferenza "mostra originale" dell'admin.
 * Legge/scrive su localStorage (chiave: 'audiq.admin.showOriginal').
 * Se localStorage non è disponibile degrada a stato in-memoria per la sessione.
 * Supporta sincronizzazione cross-tab tramite storage event.
 * @returns [show, updateShow] - bool per lo stato, callback per aggiornarlo.
 */
export function useShowOriginal(): [boolean, (next: boolean) => void] {
  const [show, setShow] = useState<boolean>(() => readInitial());

  const update = useCallback((next: boolean) => {
    setShow(next);
    try {
      window.localStorage.setItem(KEY, next ? '1' : '0');
    } catch {
      // Storage non disponibile — il setState mantiene comunque lo stato per la sessione.
    }
  }, []);

  // Sync cross-tab: cambiare il toggle in una tab si riflette nelle altre.
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key === KEY) setShow(e.newValue === '1');
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return [show, update];
}
